import { MeiliSearch } from "meilisearch";
import { MeilisearchProduct } from "../types-var/product";
import { BlogArticle } from "../types-var/blog"; // adjust path as needed

// Export the type so it can be imported elsewhere
export type SortOption =
  | "popularity:desc"
  | "price:asc"
  | "price:desc"
  | "name:asc"
  | "name:desc";

export interface SearchOptions {
  sort?: SortOption[];
  filter?: string; // Fixed: should be string, not string[]
  page?: number;
  hitsPerPage?: number;
  // Debug options for development/validation
  showRankingScore?: boolean;
  showRankingScoreDetails?: boolean;
}

const client = new MeiliSearch({
  host: process.env.REACT_APP_MEILISEARCH_HOST || "https://search.evefant.com",
  apiKey:
    process.env.REACT_APP_MEILISEARCH_API_KEY ||
    "ieerJ0Mumuusc3Tirvql5VJRwpAWxBXtvGF3mrdLTg0",
});

// Map sort options to Meilisearch sort parameters
// Note: "popularity:desc" uses undefined to enable relevance-based sorting
const SORT_MAPPING: Record<SortOption, string[] | undefined> = {
  "popularity:desc": undefined, // undefined means no sort parameter - use relevance ranking
  "price:asc": ["price.regPrice:asc"],
  "price:desc": ["price.regPrice:desc"],
  "name:asc": ["name:asc"],
  "name:desc": ["name:desc"],
};

export const configureMeilisearchIndex = async () => {
  try {
    const settings = {
      filterableAttributes: [
        "stock",
        "partner.name",
        "sku",
        "name",
        "price.regPrice",
        "price.discount",
        "isCustomizable",
        "type",
        "attributes.name",
        "attributes.values.name",
        "categories.id",
        "categories.url",
        "categories.level",
        "categories.parent.id",
        "categories.parent.url",
        "categories.name",
        "categories.parent.name",
        "categories.parent.level",
        "categories.parent.parent.url", // Add this
        "categories.parent.parent.parent.url", // Add this
        "categories.parent.parent.name",
        "categories.parent.parent.parent.name",
        "categoryPath",
        "categories.id",
        "categories.parentId",
        "categories.level",
        "categories.path",
        // Discount and promotion filterable attributes
        "hasActiveDiscount",
        "discountPercentage",
        "maxDiscountAmount",
        "promotionIds",
      ],
      sortableAttributes: ["name", "price.regPrice"],
      searchableAttributes: [
        "name",
        "description",
        "sku",
        "partner.name",
        "attributes.values.name",
        "categories.name",
        "categories.parent.name",
      ],
      distinctAttribute: null,
      typoTolerance: {
        enabled: true,
        minWordSizeForTypos: {
          oneTypo: 4,
          twoTypos: 8,
        },
        disableOnAttributes: ["attributes.values.name"],
      },
    };

    console.log("Updating Meilisearch settings...");
    await client.index("products").updateSettings(settings);
    console.log("Settings updated successfully");

    // Verify settings
    const updatedSettings = await client.index("products").getSettings();
    console.log("Current settings:", updatedSettings);
  } catch (error) {
    console.error("Failed to configure Meilisearch:", error);
  }
};

export const searchProducts = async (
  query: string,
  options: SearchOptions = {}
) => {
  try {
    const sortValue = options.sort?.[0]
      ? SORT_MAPPING[options.sort[0]]
      : undefined;

    const searchParams: any = {
      q: query,
      hitsPerPage: options.hitsPerPage || 24,
      page: options.page || 1,
      ...(options.filter?.length ? { filter: options.filter } : {}),
      // Add debugging parameters when requested
      ...(options.showRankingScore ? { showRankingScore: true } : {}),
      ...(options.showRankingScoreDetails
        ? { showRankingScoreDetails: true }
        : {}),
    };

    // Only add sort parameter if sortValue is defined and has values
    if (sortValue && sortValue.length > 0) {
      searchParams.sort = sortValue;
    }

    console.log("Search request:", { query, params: searchParams });
    const results = await client.index("products").search(query, searchParams);
    console.log("Search response:", {
      hits: results.hits.length,
      sort: sortValue,
      firstPrice: results.hits[0]?.price.regPrice,
    });

    // Verify data structure of first result
    if (results.hits.length > 0) {
      const firstProduct = results.hits[0];
      console.log("Product delivery zones:", {
        partner: firstProduct.partner,
        originZones: firstProduct.partner?.originZones,
        deliveryFeeZone: firstProduct.partner?.originZones?.deliveryFeeZone,
        deliveryLeadTimeZone:
          firstProduct.partner?.originZones?.deliveryLeadTimeZone,
      });
    }

    return results;
  } catch (error) {
    console.error("Meilisearch error:", error);
    throw error;
  }
};

// Add a helper function for category-based search
export const searchProductsByCategory = async (
  categoryUrl: string,
  options: SearchOptions = {}
) => {
  try {
    const categoryFilter = `categories.url = "${categoryUrl}"`;
    const sortValue = options.sort?.[0]
      ? SORT_MAPPING[options.sort[0]]
      : undefined;

    const searchParams: any = {
      q: "",
      hitsPerPage: options.hitsPerPage || 24,
      page: options.page || 1,
      filter: options.filter
        ? `${categoryFilter} AND ${options.filter}`
        : categoryFilter,
      // Add debugging parameters when requested
      ...(options.showRankingScore ? { showRankingScore: true } : {}),
      ...(options.showRankingScoreDetails
        ? { showRankingScoreDetails: true }
        : {}),
    };

    // Only add sort if it's defined (undefined means use relevance)
    if (sortValue !== undefined) {
      searchParams.sort = sortValue;
    }

    const results = await client.index("products").search("", searchParams);
    return results;
  } catch (error) {
    console.error("Meilisearch category search error:", error);
    throw error;
  }
};

interface SearchResponse<T> {
  hits: T[];
  totalHits: number;
  totalPages: number;
  page: number;
  hitsPerPage: number;
}

export const getProductsByCategory = async (
  { id, url, level }: { id: number; url: string; level: number },
  options: SearchOptions = {}
): Promise<SearchResponse<MeilisearchProduct>> => {
  try {
    // Get sort value and handle undefined case
    const sortValue = options.sort?.[0]
      ? SORT_MAPPING[options.sort[0]]
      : undefined;

    const searchParams: any = {
      q: "",
      hitsPerPage: options.hitsPerPage || 24,
      page: options.page || 1,
      // Use the filter conditions passed in options instead of overriding them
      ...(options.filter
        ? { filter: options.filter }
        : {
            // FIXED: Use categories.id which is available as a filterable attribute
            // This will match products that have this category ID in their categories array
            filter: `categories.id = ${id}`,
          }),
    };

    // Only add sort parameter if sortValue is defined and has values
    if (sortValue && sortValue.length > 0) {
      searchParams.sort = sortValue;
    }

    console.log("Category search request:", {
      categoryId: id,
      level,
      filter: searchParams.filter,
      sort: searchParams.sort,
      page: searchParams.page,
      hitsPerPage: searchParams.hitsPerPage,
      optionsPage: options.page,
      optionsFilter: options.filter,
    });

    const results = await client.index("products").search("", searchParams);

    return {
      hits: results.hits as MeilisearchProduct[],
      totalHits: results.totalHits || 0,
      totalPages: results.totalPages || 1,
      page: results.page || 1,
      hitsPerPage: results.hitsPerPage || 24,
    };
  } catch (error) {
    console.error("Category products fetch error:", error);
    throw error;
  }
};

export const searchBlogArticles = async (query: string, options: any) => {
  // If the sort option is popularity, remove the sort parameter so that
  // the default ranking order is used.
  if (options.sort && options.sort[0] === "popularity:desc") {
    delete options.sort;
  }
  return client.index("blog_articles").search<BlogArticle>(query, options);
};

// Dedicated functions for filter extraction - fetch ALL matching products
export const getAllProductsForCategoryFilters = async (
  { id, url, level }: { id: number; url: string; level: number },
  filters: { [key: string]: string[] } = {}
): Promise<MeilisearchProduct[]> => {
  try {
    // Convert filter object to MeiliSearch filter string, but always include base category filter
    let filterConditions: string[] = [
      `categories.id = ${id}`, // Always filter by the base category
    ];

    if (Object.keys(filters).length > 0) {
      Object.entries(filters)
        .filter(([_, values]) => values.length > 0)
        .forEach(([key, values]) => {
          if (key === "Categorías") {
            // Add additional category filters (subcategories, etc.)
            const categoryFilters = values.map(
              (categoryId) => `categories.id = ${categoryId}`
            );
            if (categoryFilters.length > 0) {
              filterConditions.push(`(${categoryFilters.join(" OR ")})`);
            }
          } else {
            const attributeFilters = values.map((value) => {
              try {
                const parsed = JSON.parse(value);
                return `(attributes.name = '${key}' AND attributes.values.name = '${parsed.name}')`;
              } catch {
                return `(attributes.name = '${key}' AND attributes.values.name = '${value}')`;
              }
            });
            if (attributeFilters.length > 0) {
              filterConditions.push(`(${attributeFilters.join(" OR ")})`);
            }
          }
        });
    }

    const searchParams = {
      q: "",
      hitsPerPage: 10000, // Fetch all products for filter extraction
      page: 1,
      filter: filterConditions.join(" AND "),
    };

    console.log("Fetching all products for category filters:", {
      categoryId: id,
      level,
      filtersProvided: Object.keys(filters),
      finalFilter: searchParams.filter,
    });

    const results = await client.index("products").search("", searchParams);
    return results.hits as MeilisearchProduct[];
  } catch (error) {
    console.error("Error fetching all products for category filters:", error);
    throw error;
  }
};

export const getAllProductsForSearchFilters = async (
  query: string,
  filters: { [key: string]: string[] } = {}
): Promise<MeilisearchProduct[]> => {
  try {
    // Convert filter object to MeiliSearch filter string
    let filterConditions: string[] = [];

    if (Object.keys(filters).length > 0) {
      Object.entries(filters)
        .filter(([_, values]) => values.length > 0)
        .forEach(([key, values]) => {
          // Use case-insensitive comparison for category filters
          if (key.toLowerCase() === "categorías") {
            const categoryFilters = values.map((categoryValue) => {
              try {
                // Parse the JSON string to extract the actual category ID
                const categoryData = JSON.parse(categoryValue);
                console.log(
                  "🔍 getAllProductsForSearchFilters - Parsed category data:",
                  categoryData
                );
                return `categories.id = ${categoryData.id}`;
              } catch (error) {
                // Fallback: if it's not JSON, treat as direct ID
                console.log(
                  "🔍 getAllProductsForSearchFilters - Using direct category ID:",
                  categoryValue
                );
                return `categories.id = ${categoryValue}`;
              }
            });
            if (categoryFilters.length > 0) {
              filterConditions.push(`(${categoryFilters.join(" OR ")})`);
            }
          } else {
            const attributeFilters = values.map((value) => {
              try {
                const parsed = JSON.parse(value);
                return `(attributes.name = '${key}' AND attributes.values.name = '${parsed.name}')`;
              } catch {
                return `(attributes.name = '${key}' AND attributes.values.name = '${value}')`;
              }
            });
            if (attributeFilters.length > 0) {
              filterConditions.push(`(${attributeFilters.join(" OR ")})`);
            }
          }
        });
    }

    const searchParams = {
      q: query,
      hitsPerPage: 10000, // Fetch all products for filter extraction
      page: 1,
      ...(filterConditions.length > 0
        ? { filter: filterConditions.join(" AND ") }
        : {}),
    };

    console.log("Fetching all products for search filters:", {
      query,
      filtersProvided: Object.keys(filters),
      filterString: searchParams.filter,
    });

    const results = await client.index("products").search(query, searchParams);
    return results.hits as MeilisearchProduct[];
  } catch (error) {
    console.error("Error fetching all products for search filters:", error);
    throw error;
  }
};
